/**
 * C18 — Prompt Studio pure helpers.
 *
 * No I/O, no AWS SDK, no Node-only APIs (this file is also imported by the
 * frontend through the Vite alias).  Every function is exported exactly once;
 * lot G's TypeScript planner / sweeper and lot I's sweep / rollup import from
 * here.  Python ports (finisher, DLQ consumer) pin golden fixtures from
 * __fixtures__/brand-allowances.golden.json.
 *
 * Sections:
 *   §1  Period helpers
 *   §2  Cadence helpers (00b DQ3)
 *   §3  Launch ids (00b DQ2)
 *   §4  Allowance resolution (00b DQ11)
 *   §5  Reserve / settle builders (00b DQ10)
 *   §6  Agency credits helpers
 *   §7  First-run slack rule (00b DQ12)
 *   §8  Dispatch mode resolver (00b DQ15)
 *   §9  Brand prompt cadence + per-launch need (C15, 00b DQ3 / DQ11)
 */

import type { Cadence, SchedulableAuditType } from '../types/cadence';
import { CADENCE_DEFAULTS, cadenceToDays, resolveCadence } from '../types/cadence';
import type { Brand } from '../types/brand';
import type { BrandQueryAxis } from '../types/brand-query';
import type {
  AllowanceKind,
  AllowanceFigures,
  NeedRow,
  NeedTerm,
  NeedTerms,
  PromptDispatchConfig,
  PromptDispatchMode,
  PromptSchedule,
  QuotaLaunchMarker,
  SaveWarnings,
  TransactItem,
} from '../types/brand-allowances';
import type { TenantLike } from '../types/brand-allowances';
import { METRIC_REGISTRY, resolveEntitlement } from '../types/metering';
import { effectiveFeatureFlags } from '../types/tenant';
import { djb2 } from './conversationSchedule';

// ---------------------------------------------------------------------------
// §1  Period helpers
// ---------------------------------------------------------------------------

/** YYYY-MM period key for a given instant. */
export function periodKey(at: Date): string {
  return at.toISOString().slice(0, 7);
}

/** UTC start of the month that contains `at` (ms). */
export function periodStart(at: Date): number {
  return Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), 1);
}

/** UTC start of the month AFTER the one containing `at` (exclusive end, ms). */
export function periodEnd(at: Date): number {
  return Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 1);
}

/** YYYY-MM-DD UTC day string. */
export function utcDay(t: string): string {
  return t.slice(0, 10);
}

// ---------------------------------------------------------------------------
// §2  Cadence helpers (00b DQ3) — ISO strings in/out, UTC throughout
// ---------------------------------------------------------------------------

/**
 * Advance `day` (YYYY-MM-DD) by one cadence step.
 * Exported because lots A, G and I call it — nobody re-implements it.
 *
 * - `days`  → + interval days
 * - `weeks` → + 7 × interval days
 * - `months` → `interval` calendar months later on day `monthAnchorDay ?? <day's own day>`,
 *   clamped to the month's last day (anchor 31 → 31 Jan, 28 Feb in a non-leap year, 31 Mar).
 */
export function addCadence(
  day: string,
  c: Exclude<Cadence, 'off'>,
  monthAnchorDay?: number,
): string {
  const d = new Date(day + 'T00:00:00Z');
  if (c.unit === 'days') {
    d.setUTCDate(d.getUTCDate() + c.interval);
  } else if (c.unit === 'weeks') {
    d.setUTCDate(d.getUTCDate() + 7 * c.interval);
  } else {
    // months
    const anchor = monthAnchorDay ?? d.getUTCDate();
    const targetMonth = d.getUTCMonth() + c.interval;
    const year = d.getUTCFullYear() + Math.floor(targetMonth / 12);
    const month = targetMonth % 12;
    // Last day of the target month
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    d.setUTCFullYear(year, month, Math.min(anchor, lastDay));
  }
  return d.toISOString().slice(0, 10);
}

/**
 * The UTC timestamp (ISO) of a slot within `day`.
 * `slotMinute` ∈ [0, 1439].
 */
export function slotTime(day: string, slotMinute: number): string {
  const h = Math.floor(slotMinute / 60);
  const m = slotMinute % 60;
  const hh = h.toString().padStart(2, '0');
  const mm = m.toString().padStart(2, '0');
  return `${day}T${hh}:${mm}:00.000Z`;
}

/**
 * The first slot at `slotMinute` strictly after instant `t`.
 * Either today's slot (if it hasn't fired yet) or tomorrow's.
 */
export function firstSlotAfter(t: string, slotMinute: number): string {
  const day = utcDay(t);
  const candidate = slotTime(day, slotMinute);
  if (candidate > t) return candidate;
  const tomorrow = addCadence(day, { interval: 1, unit: 'days' });
  return slotTime(tomorrow, slotMinute);
}

/**
 * 00b DQ3: the `nextLaunchAt` written when the cadence is first saved or changed.
 *
 * - `'off'` → `undefined` (lot G turns this into `REMOVE nextLaunchAt, dueBucket`).
 * - Otherwise: the first slot at or after `now + cadence` from the last launch
 *   (or from `now` when no last launch exists).
 */
export function nextLaunchOnCadenceWrite(opts: {
  lastLaunchAt?: string;
  cadence: Cadence;
  slotMinute: number;
  monthAnchorDay?: number;
  now: string;
}): string | undefined {
  const { lastLaunchAt, cadence, slotMinute, monthAnchorDay, now } = opts;
  if (cadence === 'off') return undefined;
  const base = lastLaunchAt ?? now;
  const candidate = slotTime(addCadence(utcDay(base), cadence, monthAnchorDay), slotMinute);
  // DQ3: never within 5 min of now (the sweeper must still see it due), never in the past.
  const floor = new Date(new Date(now).getTime() + 5 * 60_000).toISOString();
  return candidate > floor ? candidate : firstSlotAfter(floor, slotMinute);
}

/**
 * 00b DQ3: the `nextLaunchAt` written after each sweep tick.
 *
 * Takes `Exclude<Cadence, 'off'>` — a brand with no `nextLaunchAt` is never claimed.
 */
export function nextLaunchAfterTick(opts: {
  tick: string;
  cadence: Exclude<Cadence, 'off'>;
  slotMinute: number;
  monthAnchorDay?: number;
  now: string;
}): string {
  const { tick, cadence, slotMinute, monthAnchorDay, now } = opts;
  const candidate = slotTime(addCadence(utcDay(tick), cadence, monthAnchorDay), slotMinute);
  // DQ3: a candidate already past (a long outage) → the next slot after now, so a
  // claimed brand is never due again on the following tick.
  return candidate > now ? candidate : firstSlotAfter(now, slotMinute);
}

/**
 * How many times this cadence fires in one 31-day period (the formula used for
 * `sustainableNeed`).  `'off'` → 0; `months` (any interval) → 1.
 * Supersedes the old R20 (`months` → 2).
 */
export function launchesInPeriod(c: Cadence): number {
  if (c === 'off') return 0;
  if (c.unit === 'months') return 1;
  const daysPerStep = c.unit === 'weeks' ? 7 * c.interval : c.interval;
  return Math.ceil(31 / daysPerStep);
}

/**
 * How many of the next scheduled launches (starting from `nextLaunchAt`, stepping
 * by `addCadence`) fall strictly before `periodEnd`.
 *
 * `'off'`, no `nextLaunchAt`, or `nextLaunchAt >= periodEnd` → 0.
 */
export function launchesLeft(opts: {
  nextLaunchAt?: string;
  cadence: Cadence;
  monthAnchorDay?: number;
  periodEnd: string; // ISO
}): number {
  const { nextLaunchAt, cadence, monthAnchorDay, periodEnd } = opts;
  if (!nextLaunchAt || cadence === 'off' || nextLaunchAt >= periodEnd) return 0;
  let count = 0;
  let cur = nextLaunchAt;
  while (cur < periodEnd) {
    count++;
    cur = slotTime(addCadence(utcDay(cur), cadence as Exclude<Cadence, 'off'>, monthAnchorDay), 0);
    // safety: a months cadence with a very short period can't loop forever
    if (count > 366) break;
  }
  return count;
}

// ---------------------------------------------------------------------------
// §3  Launch ids (00b DQ2)
// ---------------------------------------------------------------------------

/** The UUID v5 namespace for prompt audit ids — FROZEN like a Cibles targetId. */
export const PROMPT_AUDIT_NS = '98f990fb-c2cb-42be-8027-6445670feac8';

/**
 * 00b DQ2 — derive stable, collision-free ids for a launch.
 *
 * - `launchId`    = `'pl_' + hex(sha256(utf8(launchKey))).slice(0, 32)`
 * - `auditIds[a]` = UUID v5(PROMPT_AUDIT_NS, launchId + '#' + a)
 *
 * WebCrypto only — no `node:crypto` so this file is safe to import in the
 * browser (Vite alias).  Every retry of the same `launchKey` gets the same ids.
 * Any change to the prefix, hash, namespace or `#` join is a `v2` migration.
 */
export async function promptLaunchIds(
  launchKey: string,
  axes: readonly BrandQueryAxis[],
): Promise<{ launchId: string; auditIds: Partial<Record<BrandQueryAxis, string>> }> {
  const enc = new TextEncoder();

  // launchId: SHA-256 → hex → first 32 chars
  const rawKey = await globalThis.crypto.subtle.digest('SHA-256', enc.encode(launchKey));
  const hexKey = Array.from(new Uint8Array(rawKey))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const launchId = 'pl_' + hexKey.slice(0, 32);

  // auditIds: UUID v5 per axis
  const auditIds: Partial<Record<BrandQueryAxis, string>> = {};
  for (const axis of axes) {
    const name = launchId + '#' + axis;
    const rawName = await globalThis.crypto.subtle.digest('SHA-1', enc.encode(name));
    const bytes = new Uint8Array(rawName);
    // RFC 4122 §4.3: version 5, variant 10xx
    bytes[6] = (bytes[6] & 0x0f) | 0x50; // version 5
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
    const b = (i: number) => bytes[i].toString(16).padStart(2, '0');
    auditIds[axis] =
      `${b(0)}${b(1)}${b(2)}${b(3)}-${b(4)}${b(5)}-${b(6)}${b(7)}-${b(8)}${b(9)}-${b(10)}${b(11)}${b(12)}${b(13)}${b(14)}${b(15)}`;
  }

  return { launchId, auditIds };
}

// ---------------------------------------------------------------------------
// §4  Allowance resolution (00b DQ11)
// ---------------------------------------------------------------------------

/**
 * THE one place that resolves both per-brand allowances.
 *
 * - `conversationRuns`: 0 when `conversationsEnabled` is false OR the
 *   `conversation_personas` entitlement is 0.  Otherwise the registry value.
 * - `promptRequests`: the registry value for `brand_prompt_requests`.
 *
 * Never reads `TIER_LIMITS` directly.
 */
export function resolveBrandAllowances(
  tenant: TenantLike,
): { promptRequests: number; conversationRuns: number } {
  const flags = effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags);
  const overrides = tenant.settings?.quotaOverrides;
  const packs = tenant.settings?.activePacks;

  let conversationRuns: number;
  if (!flags.conversationsEnabled) {
    conversationRuns = 0;
  } else {
    const personas = resolveEntitlement('conversation_personas', tenant.tier as any, {
      override: overrides?.conversation_personas,
      packs: packs?.conversation_personas,
    });
    if (personas.limit === 0) {
      conversationRuns = 0;
    } else {
      const cr = resolveEntitlement('brand_conversation_runs', tenant.tier as any, {
        override: overrides?.brand_conversation_runs,
        packs: packs?.brand_conversation_runs,
      });
      conversationRuns = cr.limit;
    }
  }

  const pr = resolveEntitlement('brand_prompt_requests', tenant.tier as any, {
    override: overrides?.brand_prompt_requests,
    packs: packs?.brand_prompt_requests,
  });

  return { promptRequests: pr.limit, conversationRuns };
}

/**
 * 00b DQ11 — compute the need and availability figures for one brand × kind.
 *
 * `used` and `consumed` come from the caller (a DDB read of the QUSE# counter);
 * the need is computed from the `terms` list.
 */
export function allowanceFigures(
  need: NeedTerms,
  usedFromCounter: number,
  consumedFromCounter: number,
  periodEndIso: string,
): AllowanceFigures {
  const { allowance, terms } = need;

  let perLaunch = 0;
  let sustainableNeed = 0;
  let monthNeed = 0;
  let allSameCadence = terms.length > 0;
  let firstCadence: Cadence | undefined;

  for (const t of terms) {
    perLaunch += t.perLaunch;
    const lip = launchesInPeriod(t.cadence);
    sustainableNeed += t.perLaunch * lip;

    const ll = t.nextLaunchAt
      ? launchesLeft({ nextLaunchAt: t.nextLaunchAt, cadence: t.cadence, monthAnchorDay: t.monthAnchorDay, periodEnd: periodEndIso })
      : 0;
    monthNeed += t.perLaunch * ll;

    if (allSameCadence) {
      const cadenceKey = JSON.stringify(t.cadence);
      if (firstCadence === undefined) firstCadence = t.cadence;
      else if (JSON.stringify(firstCadence) !== cadenceKey) allSameCadence = false;
    }
  }

  const used = usedFromCounter;
  const consumed = consumedFromCounter;
  const remaining = allowance === -1 ? -1 : allowance - used;
  const ok =
    allowance === -1 ||
    (sustainableNeed <= allowance && used + monthNeed <= allowance);

  return {
    allowance,
    used,
    consumed,
    remaining,
    sustainableNeed,
    monthNeed,
    perLaunch,
    launchesInPeriod: allSameCadence && firstCadence ? launchesInPeriod(firstCadence) : undefined,
    launchesLeft: allSameCadence && firstCadence && terms[0]?.nextLaunchAt
      ? launchesLeft({ nextLaunchAt: terms[0].nextLaunchAt, cadence: firstCadence, monthAnchorDay: terms[0].monthAnchorDay, periodEnd: periodEndIso })
      : undefined,
    ok,
  };
}

/**
 * 00b DQ11 — does a configuration of several terms fit (conversations: one term per cadence)?
 * `allowanceFigures` over the terms; `configFits` is its one-term case.
 */
export function configFitsTerms(i: {
  allowance: number;
  used: number;
  periodEnd: string;
  terms: NeedTerm[];
}): { ok: boolean; sustainableNeed: number; monthNeed: number } {
  const f = allowanceFigures({ allowance: i.allowance, terms: i.terms }, i.used, 0, i.periodEnd);
  return { ok: f.ok, sustainableNeed: f.sustainableNeed, monthNeed: f.monthNeed };
}

/** Alias for single-term case (prompt allowances: one cadence per brand). */
export function configFits(term: NeedTerm, allowance: number, usedFromCounter: number, periodEndIso: string): boolean {
  return configFitsTerms({ allowance, used: usedFromCounter, periodEnd: periodEndIso, terms: [term] }).ok;
}

/**
 * C17.3 — the agency balance warning of a save (prompts or conversations), never a refusal.
 * The other kind's monthly credits are summed in (one agency balance). `[]` when the change does
 * not raise the total or when the balance still covers a month of it (C17.3: the dialog is
 * « au delà de votre solde »); `agencyBalanceDays` = days the balance lasts at the new total;
 * `appliesFrom` = the next launch, only when this month's consumption rises.
 */
export function agencySaveWarnings(i: {
  kind: AllowanceKind;
  creditsPerMonthBefore: number;
  creditsPerMonthAfter: number;
  otherKindCreditsPerMonth: number;
  balance: number;
  nextLaunchAt?: string;
  raisesMonth: boolean;
}): SaveWarnings[] {
  const totalBefore = i.creditsPerMonthBefore + i.otherKindCreditsPerMonth;
  const totalAfter = i.creditsPerMonthAfter + i.otherKindCreditsPerMonth;
  if (totalAfter <= 0 || totalAfter <= totalBefore || totalAfter <= i.balance) return [];
  return [{
    kind: i.kind,
    creditsPerMonthBefore: i.creditsPerMonthBefore,
    creditsPerMonthAfter: i.creditsPerMonthAfter,
    agencyBalanceDays: Math.floor(i.balance / (totalAfter / 30)),
    appliesFrom: i.raisesMonth ? i.nextLaunchAt : undefined,
  }];
}

/**
 * 00b DQ11: how many rows of the given `axis` could be added and still fit
 * both the sustainable and month checks.  Used for the DQ11 "N prompts left" label.
 */
export function promptsThatFit(
  need: NeedTerms,
  usedFromCounter: number,
  periodEndIso: string,
): number {
  const { allowance, terms } = need;
  if (allowance === -1 || !terms[0]) return Infinity;
  const term = terms[0];
  const lip = launchesInPeriod(term.cadence);
  const ll = term.nextLaunchAt
    ? launchesLeft({ nextLaunchAt: term.nextLaunchAt, cadence: term.cadence, monthAnchorDay: term.monthAnchorDay, periodEnd: periodEndIso })
    : 0;
  const engines = need.enginesByAxis ? Object.values(need.enginesByAxis).reduce((a, b) => a + b, 0) : 0;
  if (engines === 0 || lip === 0) return Infinity;
  const byMonth = Math.floor((allowance - term.perLaunch * ll - usedFromCounter) / (lip * engines));
  const bySustainable = Math.floor((allowance - term.perLaunch * lip) / (lip * engines));
  return Math.max(0, Math.min(byMonth, bySustainable));
}

/** Conversation launch cost: sum of cells[].runs. */
export function conversationLaunchCost(cells: { runs: number }[]): number {
  return cells.reduce((sum, c) => sum + c.runs, 0);
}

// ---------------------------------------------------------------------------
// §5  Reserve / settle builders (00b DQ10)
// ---------------------------------------------------------------------------

/** QUSE# key helpers — the one place that builds these keys. */
export function quotaKeys(i: {
  tenantId: string;
  brandId: string;
  kind: AllowanceKind;
  launchKey: string;
  axisOrAll: string;
  periodKey: string;
}): { markerKey: { PK: string; SK: string }; counterKey: { PK: string; SK: string } } {
  return {
    markerKey: {
      PK: `TENANT#${i.tenantId}`,
      SK: `QUSE#L#${i.kind}#${i.launchKey}#${i.axisOrAll}`,
    },
    counterKey: {
      PK: `TENANT#${i.tenantId}`,
      SK: `QUSE#${i.brandId}#${i.kind}#${i.periodKey}`,
    },
  };
}

export const RESERVE_MAX_RETRIES = 3;

/**
 * 00b DQ10 pre-guard: run before `buildReserveLaunchItems` to short-circuit
 * cases that need no write.
 *
 *   `total <= 0`          → `'reserved'`  (nothing to write)
 *   `allowance !== -1 && total > allowance` → `'over_allowance'`
 *   otherwise             → `null`         (proceed to build items)
 */
export function reservePreGuard(
  total: number,
  allowance: number,
): 'reserved' | 'over_allowance' | null {
  if (total <= 0) return 'reserved';
  if (allowance !== -1 && total > allowance) return 'over_allowance';
  return null;
}

/**
 * 00b DQ10 — build the `TransactWriteItems` items for a reserve.
 *
 * Items: [0..n-1] one marker Put per axis with cost > 0 (axes sorted),
 * [n] one counter Update.
 *
 * The counter condition is omitted when `allowance === -1` (unlimited).
 */
export function buildReserveLaunchItems(i: {
  table: string;
  tenantId: string;
  brandId: string;
  kind: AllowanceKind;
  launchKey: string;
  launchId: string;
  periodKey: string;
  costByAxis: Record<string, number>; // axis → cost, or { all: cost } for conversations
  allowance: number;
  deadlineAt: string; // ISO
}): TransactItem[] {
  const { table, tenantId, brandId, kind, launchKey, launchId, periodKey, costByAxis, allowance, deadlineAt } = i;
  const now = Date.now();
  // TTL: end of the period month + 40 days (in seconds)
  const [yr, mo] = periodKey.split('-').map(Number);
  const endOfMonth = Date.UTC(yr, mo, 1); // exclusive end = start of next month
  const ttl = Math.floor(endOfMonth / 1000) + 40 * 86_400;

  const sortedAxes = Object.keys(costByAxis).sort();
  const items: TransactItem[] = [];

  let total = 0;
  for (const axis of sortedAxes) {
    const cost = costByAxis[axis];
    if (cost <= 0) continue;
    total += cost;

    const reaperBase = kind === 'conversationRuns' ? 110 : 100;
    const { markerKey } = quotaKeys({ tenantId, brandId, kind, launchKey, axisOrAll: axis, periodKey });

    items.push({
      Put: {
        TableName: table,
        Item: {
          ...markerKey,
          brandId,
          launchId,
          periodKey,
          cost,
          status: 'open',
          deadlineAt,
          reaperShard: reaperBase + (djb2(markerKey.SK) % 10),
          ttl,
        },
        ConditionExpression: 'attribute_not_exists(SK)',
      },
    });
  }

  if (total <= 0) return []; // nothing to reserve

  const { counterKey } = quotaKeys({ tenantId, brandId, kind, launchKey: '', axisOrAll: '', periodKey });

  const counterUpdate: TransactItem = {
    Update: {
      TableName: table,
      Key: counterKey,
      UpdateExpression: 'ADD #used :total SET #ttl = if_not_exists(#ttl, :ttl)',
      ...(allowance !== -1
        ? {
            ConditionExpression: 'attribute_not_exists(#used) OR #used <= :allowanceMinusTotal',
            ExpressionAttributeNames: { '#used': 'used', '#ttl': 'ttl' },
            ExpressionAttributeValues: {
              ':total': total,
              ':ttl': ttl,
              ':allowanceMinusTotal': allowance - total,
            },
          }
        : {
            ExpressionAttributeNames: { '#used': 'used', '#ttl': 'ttl' },
            ExpressionAttributeValues: { ':total': total, ':ttl': ttl },
          }),
    },
  };

  return [...items, counterUpdate];
}

/**
 * 00b DQ10 — classify a failed `TransactWriteItems` response for a reserve.
 *
 * The marker Puts are `[0..markerCount-1]`; the counter Update is `[markerCount]`.
 */
export function classifyReserveCancellation(
  reasons: { Code?: string }[] | undefined,
  markerCount: number,
): 'already_reserved' | 'over_allowance' | 'retry' | 'unavailable' {
  if (!reasons) return 'unavailable';
  // Check marker conditions first (a redelivery after the counter moved must not read as over_allowance)
  for (let i = 0; i < markerCount; i++) {
    if (reasons[i]?.Code === 'ConditionalCheckFailed') return 'already_reserved';
  }
  if (reasons[markerCount]?.Code === 'ConditionalCheckFailed') return 'over_allowance';
  if (reasons.some((r) => r?.Code === 'TransactionConflict')) return 'retry';
  return 'unavailable';
}

/**
 * 00b DQ10 — build the `TransactWriteItems` items for a settle.
 *
 * Items: [0] marker Update SET status=settled; [1] counter Update ADD consumed/used.
 * `0 <= ok <= cost` is a precondition (the caller counts ok from result rows).
 */
export function buildSettleItems(i: {
  table: string;
  markerKey: { PK: string; SK: string };
  counterKey: { PK: string; SK: string };
  cost: number;
  ok: number;
}): TransactItem[] {
  const { table, markerKey, counterKey, cost, ok } = i;
  if (ok < 0 || ok > cost) throw new Error(`buildSettleItems: ok=${ok} out of [0, ${cost}]`);
  const released = cost - ok;

  return [
    {
      Update: {
        TableName: table,
        Key: markerKey,
        UpdateExpression: 'SET #status = :settled, #ok = :ok, #released = :released REMOVE reaperShard, deadlineAt',
        ConditionExpression: '#status = :open',
        ExpressionAttributeNames: { '#status': 'status', '#ok': 'ok', '#released': 'released' },
        ExpressionAttributeValues: { ':settled': 'settled', ':ok': ok, ':released': released, ':open': 'open' },
      },
    },
    {
      Update: {
        TableName: table,
        Key: counterKey,
        UpdateExpression: 'ADD #consumed :ok, #used :minusReleased',
        ConditionExpression: 'attribute_exists(PK)',
        ExpressionAttributeNames: { '#consumed': 'consumed', '#used': 'used' },
        ExpressionAttributeValues: { ':ok': ok, ':minusReleased': -released },
      },
    },
  ];
}

/**
 * 00b DQ10 — classify a failed `TransactWriteItems` response for a settle.
 */
export function classifySettleCancellation(
  reasons: { Code?: string }[] | undefined,
): 'already_settled' | 'retry' | 'unavailable' {
  if (!reasons) return 'unavailable';
  if (reasons[0]?.Code === 'ConditionalCheckFailed') return 'already_settled';
  if (reasons.some((r) => r?.Code === 'TransactionConflict')) return 'retry';
  return 'unavailable';
}

// ---------------------------------------------------------------------------
// §6  Agency credits helpers
// ---------------------------------------------------------------------------

/**
 * Prompt credits per month for a brand (agency only; self-serve tenants do not
 * have a credit cost per prompt).
 *
 * `launchesInPeriod` × `perLaunch` × `creditCostPerRun`.
 * The lot G engine cost is `engines.length` per axis per prompt per launch;
 * this helper is called with the already-computed `perLaunch`.
 */
export function agencyPromptCreditsPerMonth(
  term: { perLaunch: number; cadence: Cadence },
  creditCostPerRun = 1,
): number {
  return launchesInPeriod(term.cadence) * term.perLaunch * creditCostPerRun;
}

// ---------------------------------------------------------------------------
// §7  First-run slack rule (00b DQ12)
// ---------------------------------------------------------------------------

/**
 * 00b DQ12 — does the month's budget still have room for a first run?
 *
 * True when `sustainableNeed + perLaunch <= allowance` (or allowance is −1).
 * Non-agency only (agency always runs at the next cadence launch).
 */
export function slackRuleFits(
  figures: AllowanceFigures,
  newRowPerLaunch: number,
): boolean {
  if (figures.allowance === -1) return true;
  return figures.sustainableNeed + newRowPerLaunch <= figures.allowance;
}

// ---------------------------------------------------------------------------
// §8  Dispatch mode resolver (00b DQ15)
// ---------------------------------------------------------------------------

/**
 * THE one function that resolves a tenant's prompt dispatch mode from the stored
 * config item.  Absent item → `'legacy'`.
 *
 * Lot G's Trigger Lambda, sweeper and planner import this directly from the
 * shared utils (not from the API service which wraps it with a DDB read).
 */
export function resolvePromptDispatchMode(
  item: PromptDispatchConfig | undefined,
  tenantId: string,
): PromptDispatchMode {
  return item?.tenants?.[tenantId] ?? item?.default ?? 'legacy';
}

// ---------------------------------------------------------------------------
// §9  Brand prompt cadence + per-launch need (C15, 00b DQ3 / DQ11)
// ---------------------------------------------------------------------------

const BRAND_PROMPT_KEYS: readonly SchedulableAuditType[] = ['brand_narrative', 'brand_authority', 'brand_referral'];

/** C15 / 00b DQ3: PSCHED.cadence when the brand has a PSCHED; else (a 'legacy' tenant, or before the brand's
 *  DQ15 migration) C15's rule over resolveCadence(cadenceOverride, tier, `brand_${axis}`) of the THREE
 *  schedulable keys (brand_narrative / _authority / _referral; `comparison` has none, C1): non-agency → the
 *  FASTEST non-'off' (most launchesInPeriod, tie → shorter interval); agency → the SLOWEST, 'off' counting as
 *  slowest (fewest launchesInPeriod, tie → longer interval), so its credit spend never rises without consent.
 *  That is the value the migration writes into PSCHED. Either way a
 *  non-agency 'off' resolves to CADENCE_DEFAULTS[tier].brand_narrative (U3); agency with nothing set → 'off'.
 *  THE one definition: lot G's migration, brand create and 'legacy' footer import it (no promptCadenceOf). */
export function brandPromptCadence(
  tier: string,
  brand: Pick<Brand, 'auditConfig'>,
  psched?: Pick<PromptSchedule, 'cadence'>,
): Cadence {
  const agency = tier === 'agency';
  // Same fallback as resolveCadence's own non-agency 'off' rule.
  const tierDefault = (): Cadence => CADENCE_DEFAULTS[tier]?.brand_narrative ?? { interval: 1, unit: 'days' };

  if (psched) return psched.cadence === 'off' && !agency ? tierDefault() : psched.cadence;

  const keys = BRAND_PROMPT_KEYS.map((k) => resolveCadence(brand.auditConfig?.cadenceOverride, tier, k));
  if (agency) {
    if (keys.some((c) => c === 'off')) return 'off';
    const on = keys as Exclude<Cadence, 'off'>[];
    return on.reduce((slowest, c) => {
      const d = launchesInPeriod(c) - launchesInPeriod(slowest);
      return d < 0 || (d === 0 && cadenceToDays(c) > cadenceToDays(slowest)) ? c : slowest;
    });
  }
  const on = keys.filter((c): c is Exclude<Cadence, 'off'> => c !== 'off');
  if (on.length === 0) return tierDefault();
  return on.reduce((fastest, c) => {
    const d = launchesInPeriod(c) - launchesInPeriod(fastest);
    return d > 0 || (d === 0 && cadenceToDays(c) < cadenceToDays(fastest)) ? c : fastest;
  });
}

/** 00b DQ11: Σ over status 'active' rows of engines[row.axis]; axis 'pending' → the max over the FOUR axes
 *  (C17.7). Every archived row (any archivedReason) counts 0. */
export function perLaunch(rows: NeedRow[], engines: Record<BrandQueryAxis, number>): number {
  const maxEngines = Math.max(engines.narrative, engines.authority, engines.referral, engines.comparison);
  let total = 0;
  for (const r of rows) {
    if (r.status !== 'active') continue;
    total += r.axis === 'pending' ? maxEngines : engines[r.axis];
  }
  return total;
}
